import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useFarms, createFarmLocal } from "@/lib/repo";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Plus, MapPin, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

export default function Farms() {
  const { user } = useAuth();
  const farms = useFarms();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", location: "" });
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const create = async () => {
    if (!form.name) return toast.error("Nombre requerido");
    setSaving(true);
    try { await createFarmLocal(form); setOpen(false); setForm({ name: "", location: "" }); toast.success("Finca creada"); }
    catch (e) { toast.error(e?.userMessage || "Error"); }
    finally { setSaving(false); }
  };

  const canCreate = user?.role === "owner";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Fincas</h1>
          <p className="text-sm text-muted-foreground">{farms.length} {farms.length === 1 ? "finca" : "fincas"}</p>
        </div>
        {canCreate && (
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button className="h-10 bg-emerald-700 hover:bg-emerald-800" data-testid="farms-new-button"><Plus size={16} className="mr-1"/>Nueva finca</Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="rounded-t-2xl">
              <SheetHeader><SheetTitle>Nueva finca</SheetTitle></SheetHeader>
              <div className="py-3 space-y-3">
                <div><Label>Nombre</Label><Input data-testid="farms-new-name" value={form.name} onChange={(e)=>setForm({ ...form, name: e.target.value })} className="h-11" /></div>
                <div><Label>Ubicación</Label><Input data-testid="farms-new-location" value={form.location} onChange={(e)=>setForm({ ...form, location: e.target.value })} className="h-11" /></div>
                <Button onClick={create} disabled={saving} className="h-12 w-full bg-emerald-700 hover:bg-emerald-800" data-testid="farms-new-submit">Guardar</Button>
              </div>
            </SheetContent>
          </Sheet>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {farms.length === 0 && (
          <div className="col-span-full bg-card border border-dashed border-border rounded-2xl p-10 text-center">
            <h3 className="font-heading text-lg font-semibold mb-1">Sin fincas todavía</h3>
            <p className="text-sm text-muted-foreground mb-4">Una finca agrupa lotes y animales.</p>
            {canCreate && <Button onClick={()=>setOpen(true)} className="bg-emerald-700 hover:bg-emerald-800">Crear primera finca</Button>}
          </div>
        )}
        {farms.map((f) => (
          <Card key={f.id} className="hover:border-emerald-300 transition-colors cursor-pointer" onClick={()=>navigate(`/fincas/${f.id}`)}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-800 grid place-items-center"><MapPin size={20} /></div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-semibold text-foreground">{f.name}</div>
                <div className="text-xs text-muted-foreground">{f.location || "sin ubicación"}</div>
              </div>
              <ChevronRight size={18} className="text-muted-foreground" />
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
